import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Chip, Typography } from "@mui/material";
import { type FC, useEffect, useState } from "react";
import { useLocation } from "react-router";

import { EventPageSections } from "~/feature/event/constants";
import type { IAggregatedEvent, IOfficialPlatformsRules } from "~/types/event";

import * as Styles from "./OfficialPlatformRules.styles";

interface IOfficialPlatformRulesProps {
  event: IAggregatedEvent;
}

export const OFFICIAL_RULES_TITLE = "View full platform rules";

const hasContent = (rule: IOfficialPlatformsRules) =>
  Boolean(rule.rawSource?.trim() || rule.eventLevelSummary?.trim());

interface IPlatformRulesProps {
  rule: IOfficialPlatformsRules;
  index: number;
}

const PlatformRules: FC<IPlatformRulesProps> = ({ rule }) => {
  return (
    <Styles.PlatformBlock component="section">
      <Typography
        component="h3"
        variant="subtitle1"
        sx={{
          fontWeight: 700,
          whiteSpace: "pre-line",
          wordBreak: "break-word"
        }}>
        {rule.platform}
      </Typography>

      <Typography
        component="p"
        variant="caption"
        sx={{
          color: "text.secondary",
          whiteSpace: "pre-line",
          wordBreak: "break-word"
        }}>
        {rule.eventLevelSummary?.trim() || rule.rawSource?.trim() || ""}
      </Typography>
    </Styles.PlatformBlock>
  );
};

export const OfficialPlatformRules: FC<IOfficialPlatformRulesProps> = ({
  event,
}) => {
  const { hash } = useLocation();

  const rules = (event?.officialRules ?? []).filter(hasContent);
  const isTargetedByAnchor = hash === `#${EventPageSections.OfficialRules}`;
  const [expanded, setExpanded] = useState(isTargetedByAnchor);

  useEffect(() => {
    if (!isTargetedByAnchor) {
      return;
    }

    const frame = window.requestAnimationFrame(() => {
      setExpanded(true);
    });

    return () => window.cancelAnimationFrame(frame);
  }, [isTargetedByAnchor]);

  if (!rules.length) {
    return null;
  }

  return (
    <Styles.Root component="section" id={EventPageSections.OfficialRules}>
      <Styles.StyledAccordion
        expanded={expanded}
        onChange={() => setExpanded((prev) => !prev)}
        disableGutters
      >
        <Styles.Summary expandIcon={<ExpandMoreIcon />}>
          <Typography component="h2" variant="subtitle1" sx={{
            fontWeight: 600
          }}>
            {OFFICIAL_RULES_TITLE}
          </Typography>
          <Chip label={rules.length} size="small" color="default" />
        </Styles.Summary>

        <Styles.Details>
          {rules.map((rule, index) => (
            <PlatformRules key={rule.platform} rule={rule} index={index} />
          ))}
        </Styles.Details>
      </Styles.StyledAccordion>
    </Styles.Root>
  );
};
